'use client';

import { useState } from 'react';
import {
  WebPreview,
  WebPreviewBody,
  WebPreviewNavigation,
  WebPreviewNavigationButton,
  WebPreviewUrl,
} from '@/components/ai-elements/web-preview';
import {
  SandboxTabContent,
  SandboxTabs,
  SandboxTabsBar,
  SandboxTabsList,
  SandboxTabsTrigger,
} from '@/components/ai-elements/sandbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Copy, ExternalLink, Home, RefreshCw } from 'lucide-react';
import { Eye, WarningCircle } from '@phosphor-icons/react';
import { WorkspaceProvider } from '@/components/workspace-files/workspace-context';
import { WorkspaceFilesTab } from '@/components/workspace-files/workspace-files-tab';
import { WorkspaceDetailsTab } from '@/components/workspace-files/workspace-details-tab';

interface SandboxPreviewRendererProps {
  input?: Record<string, unknown>;
  output?: unknown;
  displayContext?: 'inline' | 'artifact';
  /** Chat session id — resolves the canonical workspace for Files/Details. */
  sessionId?: string | null;
}

function getRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function getString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getPreviewUrl(output: unknown) {
  const out = getRecord(output);
  const preview = getRecord(out?.preview);
  return getString(out?.url) ?? getString(preview?.url);
}

function getPort(input: unknown, output: unknown) {
  const fromInput = getRecord(input);
  const fromOutput = getRecord(output);
  const value = fromInput?.port ?? fromOutput?.port;
  return typeof value === 'number' ? String(value) : getString(value);
}

function getSandboxName(input: unknown, output: unknown) {
  const fromInput = getRecord(input);
  const out = getRecord(output);
  const sandbox = getRecord(out?.sandbox);
  return getString(fromInput?.name) ?? getString(sandbox?.name);
}

function NoPreviewState() {
  return (
    <div className="grid h-full place-items-center p-6">
      <div className="max-w-sm rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-4">
        <div className="flex items-start gap-3">
          <WarningCircle className="mt-0.5 size-5 text-amber-500" />
          <div className="min-w-0">
            <p className="text-sm font-medium">No live preview URL</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The sandbox may be stopped, expired, or missing a preview URL for the requested
              port. Your files are still available in the Files tab — no sandbox required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SandboxPreviewRenderer({
  input,
  output,
  displayContext = 'inline',
  sessionId,
}: SandboxPreviewRendererProps) {
  const previewUrl = getPreviewUrl(output);
  const port = getPort(input, output);
  const sandboxName = getSandboxName(input, output);
  const [currentUrl, setCurrentUrl] = useState(previewUrl ?? '');
  const [frameKey, setFrameKey] = useState(0);
  const [shellKey, setShellKey] = useState(0);
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl || previewUrl || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const resetHome = () => {
    if (!previewUrl) return;
    setCurrentUrl(previewUrl);
    setFrameKey((value) => value + 1);
    setShellKey((value) => value + 1);
  };

  const openUrl = () => {
    if (!previewUrl && !currentUrl) return;
    window.open(currentUrl || previewUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background',
        displayContext === 'artifact' ? 'h-full w-full rounded-none border-0' : 'h-[560px]',
      )}
    >
      {displayContext !== 'artifact' && (
        <div className="flex min-h-11 items-center gap-3 border-b border-border bg-muted/30 px-3">
          <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background">
            <Eye weight="duotone" className="size-4 text-primary" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{sandboxName ?? 'Workspace'}</p>
            {port && <p className="truncate text-xs text-muted-foreground">Port {port}</p>}
          </div>
        </div>
      )}

      <WorkspaceProvider sessionId={sessionId ?? null}>
        <SandboxTabs defaultValue={previewUrl ? 'preview' : 'files'} className="h-full min-h-0">
          <SandboxTabsBar>
            <SandboxTabsList>
              <SandboxTabsTrigger value="preview">Preview</SandboxTabsTrigger>
              <SandboxTabsTrigger value="files">Files</SandboxTabsTrigger>
              <SandboxTabsTrigger value="details">Details</SandboxTabsTrigger>
            </SandboxTabsList>
          </SandboxTabsBar>

          <SandboxTabContent value="preview" className="h-[calc(100%-41px)] min-h-0">
            {previewUrl ? (
              <WebPreview
                key={shellKey}
                defaultUrl={previewUrl}
                onUrlChange={setCurrentUrl}
                className="rounded-none border-0"
              >
                <WebPreviewNavigation className="gap-2 border-b bg-background/95 p-2">
                  <WebPreviewNavigationButton tooltip="Home" onClick={resetHome}>
                    <Home className="size-4" />
                  </WebPreviewNavigationButton>
                  <WebPreviewNavigationButton
                    tooltip="Refresh"
                    onClick={() => setFrameKey((value) => value + 1)}
                  >
                    <RefreshCw className="size-4" />
                  </WebPreviewNavigationButton>
                  <WebPreviewUrl aria-label="Preview URL" />
                  <WebPreviewNavigationButton tooltip={copied ? 'Copied' : 'Copy URL'} onClick={copyUrl}>
                    <Copy className="size-4" />
                  </WebPreviewNavigationButton>
                  <WebPreviewNavigationButton tooltip="Open in new tab" onClick={openUrl}>
                    <ExternalLink className="size-4" />
                  </WebPreviewNavigationButton>
                </WebPreviewNavigation>
                <WebPreviewBody
                  key={frameKey}
                  title="Sandbox preview"
                  className="h-full w-full border-0 bg-white"
                  allow="clipboard-read; clipboard-write; fullscreen"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-modals"
                />
              </WebPreview>
            ) : (
              <NoPreviewState />
            )}
          </SandboxTabContent>

          <SandboxTabContent value="files" className="h-[calc(100%-41px)] min-h-0">
            <WorkspaceFilesTab sandboxOutput={output} />
          </SandboxTabContent>

          <SandboxTabContent value="details" className="h-[calc(100%-41px)] min-h-0">
            <WorkspaceDetailsTab sandboxOutput={output} />
          </SandboxTabContent>
        </SandboxTabs>
      </WorkspaceProvider>

      {displayContext !== 'artifact' && previewUrl && (
        <div className="border-t border-border px-3 py-2">
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={openUrl}>
            <ExternalLink className="size-3.5" />
            Open preview
          </Button>
        </div>
      )}
    </div>
  );
}
